// src/store/configStore.ts

import { create } from 'zustand';
import { db, FLIP_COIN_VALUE_COP } from '@/db/db';
import type { AppSettings, BusinessType, NaranjaPlus, PlanType } from '@/db/db';
import { C, type Centavos } from '@/core/financial-engine/money';
import {
  CURRENCY_CONFIG,
  BRANCH_LIMITS_BY_PLAN,
  TAX_JURISDICTIONS,
} from '@/config/constants';

/** Jurisdicción fiscal soportada (estrategia consumida por `taxPayroll.ts`). */
export type TaxJurisdiction =
  (typeof TAX_JURISDICTIONS)[keyof typeof TAX_JURISDICTIONS];

/**
 * LocalStorage key de la jurisdicción fiscal.
 *
 * ⚠️ `tax_jurisdiction` NO existe en `AppSettings` (ver `src/db/db.ts`). Hasta
 * que se proponga/apruebe la migración Dexie v111 que lo agrega, la preferencia
 * se guarda localmente (no se persiste como columna "garantizada").
 */
const TAX_JURISDICTION_KEY = 'niiz_tax_jurisdiction';

const readStoredJurisdiction = (): TaxJurisdiction => {
  try {
    const raw = localStorage.getItem(TAX_JURISDICTION_KEY);
    if (raw === TAX_JURISDICTIONS.COLOMBIA_DIAN) return TAX_JURISDICTIONS.COLOMBIA_DIAN;
    if (raw === TAX_JURISDICTIONS.INTERNATIONAL_STANDARD) return TAX_JURISDICTIONS.INTERNATIONAL_STANDARD;
  } catch {
    /* almacenamiento no disponible */
  }
  return TAX_JURISDICTIONS.INTERNATIONAL_STANDARD;
};

const persistJurisdiction = (jurisdiction: TaxJurisdiction): void => {
  try {
    localStorage.setItem(TAX_JURISDICTION_KEY, jurisdiction);
  } catch {
    /* almacenamiento no disponible */
  }
};

const isTaxJurisdiction = (value: unknown): value is TaxJurisdiction =>
  value === TAX_JURISDICTIONS.COLOMBIA_DIAN ||
  value === TAX_JURISDICTIONS.INTERNATIONAL_STANDARD;

// ─────────────────────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────────────────────
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isPlanType = (value: unknown): value is PlanType =>
  value === 'FREE' ||
  value === 'NARANJA_PLUS' ||
  value === 'ADVANCED_ACCOUNTING' ||
  value === 'STUDIO_FIRM';

const isBusinessType = (value: unknown): value is BusinessType =>
  value === 'RETAIL' ||
  value === 'RESTAURANT' ||
  value === 'PROPERTY' ||
  value === 'RENTAL' ||
  value === 'HOSPITALITY' ||
  value === 'LOGISTICS' ||
  value === 'ACCOUNTING_FIRM';

// Configuración por defecto (Fallback)
const DEFAULT_SETTINGS: Omit<AppSettings, 'id' | 'created_at'> = {
  business_id: 'local',
  business_name: 'Mi Negocio NIIZ',
  business_type: 'RETAIL',
  currency_symbol: '$',
  tax_rate: 19,
  address: '',
  // 📍 Ubicación del negocio (proveedores, envíos, subastas).
  country: 'Colombia',
  city: '',
  phone: '',
  logo_url: null,
  bank_accounts: [],
  
  // ── 🆕 CONFIGURACIÓN DE MONEDA INTERNACIONAL v109 ──
  currency_code: CURRENCY_CONFIG.DEFAULT_CODE,
  currency_symbol_display: CURRENCY_CONFIG.DEFAULT_SYMBOL,
  decimal_separator: CURRENCY_CONFIG.DEFAULT_DECIMAL_SEPARATOR,
  thousand_separator: CURRENCY_CONFIG.DEFAULT_THOUSAND_SEPARATOR,

  // ⚠️ JURISDICCIÓN FISCAL: no es columna de AppSettings (ver TAX_JURISDICTION_KEY).
  
  // Nuevos campos de Suscripción
  plan_type: 'FREE',
  subscription_status: 'ACTIVE',
  beta_type: 'NONE',
  auto_renew: true,
  grace_period_started_at: null,
  ai_local_counter: 0,
  cloud_included_mb: 5120,
  cloud_extra_gb: 0,
  
  // Módulos
  ai_enabled: true,  // IA activa por defecto (se cobra por consumo en Flip)
  marketplace_enabled: false,
  developer_mode: false,

  // Sistema de créditos REAL: 1 Flip Coin = $100 COP (VALOR OFICIAL FIJO).
  // La migración v106 fuerza este valor si la instalación tenía 50, null o menos.
  credit_value_cop: 100,
  // Costo dinámico de IA: COP por cada 1000 tokens (prompt + respuesta). Default conservador.
  ai_cost_per_1000_tokens: 500,   // ej: 2000 tokens ≈ 1.000 COP ≈ 20 coins
  ai_cost_margin_percent: 20,     // +20% de respaldo en consultas globales (IA Comercial)

  // Facturación de almacenamiento en la nube (cobro por consumo):
  //   costo real de Supabase por GB (COP) + margen fijo de NIIZ por GB (COP).
  cloud_cost_per_gb_cop: 85,      // referencia tarifa pública ~$0.021 USD/GB-mes
  cloud_margin_per_gb_cop: 2000,  // margen fijo (tu ganancia) por GB
  ai_costs: {
    GENERAL: 200,
    INVENTORY: 300,
    SALES: 300,
    CUSTOMERS: 250,
    FINANCIAL: 400,
    ANALYTICS: 400,
    MARKET: 2000, // (legacy, se recalcula dinámico por tokens)
  },
  wompi_payment_link: 'https://checkout.nequi.wompi.co/l/V3dgEe',

  // ⚡ Venta ultrarrápida: por defecto NO imprime ticket para mantener el
  // flujo de cero fricción. Se activa desde la barra de Venta Ultrarrápida.
  quick_sale_auto_print: false,
  // ⚡ Venta ultrarrápida: por defecto cobra directo (sin modal de cambio).
  quick_sale_show_change_modal: false,
  // ⚡ Cobro global fuera de la venta ultrarrápida: preguntar efectivo/transferencia.
  quick_sale_payment_mode: 'ASK',

  // Links Wompi de MONTO FIJO (definidos por el dueño)
  wompi_5000: 'https://checkout.nequi.wompi.co/l/CuJOEh',
  wompi_10000: 'https://checkout.nequi.wompi.co/l/S1pfJF',
  wompi_20000: 'https://checkout.nequi.wompi.co/l/qxlZuQ',
  wompi_80000: 'https://checkout.nequi.wompi.co/l/V3YrE1',
  // WhatsApp para pedir links personales (montos superiores)
  wompi_whatsapp: '573009701928',

  // Tienda Digital
  store_enabled: false,
  store_name: '',
  store_description: '',
  store_whatsapp: '',
  store_banner_color: '#4f46e5',
  store_active_template: 'default',
  store_ai_enabled: false, // IA en tienda: OPCIONAL, la activa el dueño
  store_schedule: {
    enabled: false,
    days: {
      lunes:    { open: '08:00', close: '18:00', active: true },
      martes:   { open: '08:00', close: '18:00', active: true },
      miercoles:{ open: '08:00', close: '18:00', active: true },
      jueves:   { open: '08:00', close: '18:00', active: true },
      viernes:  { open: '08:00', close: '18:00', active: true },
      sabado:   { open: '09:00', close: '14:00', active: true },
      domingo:  { open: '00:00', close: '00:00', active: false },
    }
  }
};

// ─────────────────────────────────────────────────────────────
// 🔗 MIGRACIÓN LOCAL DE business_id (fuente única de verdad = sesión)
// Mueve la suscripción Naranja Plus local al id de la sesión activa para que
// la tienda digital no quede "sin suscripción" por un id desfasado.
// ─────────────────────────────────────────────────────────────
const migrateLocalBusinessId = async (oldId: string, newId: string): Promise<void> => {
  if (!oldId || !newId || oldId === newId) return;
  try {
    const oldRow = await db.naranja_plus.where('business_id').equals(oldId).first();
    if (!oldRow) return;

    const newRow = await db.naranja_plus.where('business_id').equals(newId).first();
    if (newRow) {
      // Se conserva la suscripción con vencimiento más lejano y se descarta la vieja.
      const oldExpires = new Date(String(oldRow.expires_at || 0)).getTime() || 0;
      const newExpires = new Date(String(newRow.expires_at || 0)).getTime() || 0;
      if (oldExpires > newExpires) {
        // `UpdateSpec<T>` de Dexie tipa cada campo como `Required<T>[K]`, por eso
        // `null | undefined` choca con `exactOptionalPropertyTypes`. Solo se
        // incluye `last_charge_at` cuando la fila vieja SI tiene valor.
        const chargePatch: Pick<NaranjaPlus, 'last_charge_at'> =
          oldRow.last_charge_at == null ? {} : { last_charge_at: oldRow.last_charge_at };
        await db.naranja_plus.update(newRow.id, {
          status: oldRow.status,
          expires_at: oldRow.expires_at,
          auto_renew: oldRow.auto_renew,
          ...chargePatch,
          sync_status: 'LOCAL',
        });
      }
      await db.naranja_plus.delete(oldRow.id);
      console.log(`🍊 [NARANJA PLUS] Suscripción consolidada en business_id=${newId}`);
    } else {
      // No hay fila con el id nuevo: se renombra la existente.
      await db.naranja_plus.update(oldRow.id, { business_id: newId, sync_status: 'LOCAL' });
      console.log(`🍊 [NARANJA PLUS] Suscripción local migrada: ${oldId} → ${newId}`);
    }
  } catch (e) {
    console.warn('No se pudo migrar la suscripción local al business_id de la sesión:', e);
  }
};

interface ConfigState {
  settings: AppSettings | null;
  isLoading: boolean;
  error: string | null;

  // Acciones
  loadSettings: () => Promise<void>;
  updateSettings: (updates: Partial<AppSettings>) => Promise<void>;

  // Helpers de UI
  formatCurrency: (amount: number) => string;
  getTaxRate: () => number;
  
  // ── 🆕 HELPERS FINANCIEROS v109 ──
  /** Convierte un monto en pesos a Centavos (BigInt). */
  toCentavos: (amount: number) => Centavos;
  /** Formatea moneda usando los separadores personalizados del negocio. */
  formatCurrencyCustom: (amount: number) => string;
  /** Retorna la jurisdicción fiscal configurada. */
  getJurisdiction: () => TaxJurisdiction;
  /** Retorna el límite de sucursales del plan actual. */
  getBranchLimit: () => number;
  /** Verifica si el plan actual permite Zanahoria Studio. */
  isStudioPlan: () => boolean;
  /** Retorna la configuración completa de moneda del negocio. */
  getCurrencyConfig: () => {
    code: string;
    symbol: string;
    decimalSeparator: string;
    thousandSeparator: string;
  };
}

// ─────────────────────────────────────────────────────────────
// STORE IMPLEMENTACIÓN
// ─────────────────────────────────────────────────────────────
export const useConfigStore = create<ConfigState>((set, get) => ({
  settings: null,
  isLoading: true,
  error: null,

  loadSettings: async () => {
    set({ isLoading: true, error: null });

    try {
      // El plan y la suscripción son autoritativos en Supabase. Antes de leer
      // Dexie, sincroniza el snapshot del servidor para no mostrar un FREE
      // viejo después de un cambio de plan.
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        try {
          const { refreshWallet } = await import('@/lib/wallet');
          await refreshWallet();
        } catch (planSyncError) {
          console.warn('No se pudo sincronizar el plan desde la nube:', planSyncError);
        }
      }

      // 1. Configuración del negocio activo (no la primera fila de Dexie,
      //    que puede pertenecer a otro negocio en equipos multinegocio).
      const { useAuthStore } = await import('@/store/authStore');
      let storedBusinessId = '';
      try {
        storedBusinessId = String(localStorage.getItem('niiz_active_business_id') || '').trim();
      } catch {
        /* almacenamiento no disponible */
      }
      const activeBusinessId = String(
        useAuthStore.getState().user?.business_id || storedBusinessId || '',
      ).trim();
      const config = activeBusinessId
        ? await db.settings.where('business_id').equals(activeBusinessId).first()
        : await db.settings.toCollection().first();

      if (config) {
        // MERGE con los defaults: rellena campos nuevos (ej. links Wompi) que
        // no existían en configuraciones guardadas antes, SIN perder lo guardado.
        const merged: AppSettings = { ...DEFAULT_SETTINGS, ...config };
        let needsPersist = false;

        // 💱 VALOR OFICIAL DEL FLIP COIN: $100 COP. Si la instalación quedó con
        // 50, null o cualquier otro valor (versiones viejas), se CORRIGE y se
        // persiste aquí además de la migración v106 de Dexie.
        const coinValue = Number(merged.credit_value_cop ?? 0);
        const coinFixed = !Number.isFinite(coinValue) || coinValue !== FLIP_COIN_VALUE_COP;
        if (coinFixed) {
          console.log(`💱 [FLIP] Valor del coin corregido: ${coinValue || 'sin valor'} → ${FLIP_COIN_VALUE_COP} COP`);
          merged.credit_value_cop = FLIP_COIN_VALUE_COP;
          needsPersist = true;
        }

        // ── 🆕 NORMALIZACIÓN DE MONEDA v109 ──
        // Si los campos de moneda internacional faltan, se rellenan con defaults.
        if (!merged.currency_code) {
          merged.currency_code = CURRENCY_CONFIG.DEFAULT_CODE;
          needsPersist = true;
        }
        if (!merged.currency_symbol_display) {
          merged.currency_symbol_display = CURRENCY_CONFIG.DEFAULT_SYMBOL;
          needsPersist = true;
        }
        if (!merged.decimal_separator) {
          merged.decimal_separator = CURRENCY_CONFIG.DEFAULT_DECIMAL_SEPARATOR;
          needsPersist = true;
        }
        if (!merged.thousand_separator) {
          merged.thousand_separator = CURRENCY_CONFIG.DEFAULT_THOUSAND_SEPARATOR;
          needsPersist = true;
        }
        // 🌎 JURISDICCIÓN FISCAL: preferencia LOCAL (no existe en AppSettings).
        const storedJurisdiction = readStoredJurisdiction();
        const legacyJurisdiction: unknown = Reflect.get(config, 'tax_jurisdiction');
        if (legacyJurisdiction === undefined) {
          persistJurisdiction(storedJurisdiction);
        } else if (isTaxJurisdiction(legacyJurisdiction)) {
          // Instalación que llegó a tener el campo persistido: se migra a local.
          persistJurisdiction(legacyJurisdiction);
        }

        // 🔗 FUENTE ÚNICA DE VERDAD: el `business_id` de la SESIÓN ACTIVA manda.
        // Si los ajustes locales quedaron con un id viejo (de otro registro o de
        // una instalación anterior), la tienda digital mostraría un enlace
        // desfasado. Se corrige aquí y se migra la suscripción Naranja Plus local.
        try {
          const { useAuthStore } = await import('@/store/authStore');
          const sessionBusinessId = String(useAuthStore.getState().user?.business_id || '').trim();
          const currentBusinessId = String(merged.business_id || '').trim();
          if (sessionBusinessId && sessionBusinessId !== 'local' && sessionBusinessId !== currentBusinessId) {
            console.warn(`🔗 [SETTINGS] business_id desfasado: "${currentBusinessId || 'vacío'}" → "${sessionBusinessId}" (sesión activa). Se corrige.`);
            // La suscripción Naranja Plus local se mueve al id de la sesión para
            // que la tienda no se cierre por "sin suscripción".
            await migrateLocalBusinessId(currentBusinessId, sessionBusinessId);
            merged.business_id = sessionBusinessId;
            needsPersist = true;
          }
        } catch (e) {
          console.warn('No se pudo sincronizar el business_id con la sesión:', e);
        }

        set({ settings: merged, isLoading: false });
        // Persistir el merge solo si había campos nuevos faltantes
        const hasMissingDefaults = (Object.keys(DEFAULT_SETTINGS))
          .some(k => Reflect.get(config, k) === undefined && Reflect.get(DEFAULT_SETTINGS, k) !== undefined);
        if (hasMissingDefaults || needsPersist) {
          // `AppSettings` completo también es aceptable como UpdateSpec y evita
          // que los campos opcionales queden fueran del tipo (Todos son reales).
          await db.settings.put(merged);
        }
      } else {
        // 2. Si no existe (caso raro, pues RegisterPage la crea), inicializar por defecto
        // Esto sirve como fallback si el usuario borró la DB manualmente
        const registeredType = localStorage.getItem('niiz_business_type');
        
        const newConfig: AppSettings = {
          id: generateId(),
          created_at: new Date().toISOString(),
          ...DEFAULT_SETTINGS,
          business_type: isBusinessType(registeredType) ? registeredType : 'RETAIL',
        };

        await db.settings.add(newConfig);
        set({ settings: newConfig, isLoading: false });
        
        localStorage.removeItem('niiz_business_type');
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
      set({ error: 'No se pudo cargar la configuración', isLoading: false });
    }
  },

  updateSettings: async (updates) => {
    const { settings } = get();
    
    if (!settings?.id) {
      console.warn('Intentando actualizar settings sin inicializar');
      return; 
    }

    try {
      // Evitar sobrescribir ID o fecha de creación
      const { id: _id, created_at: _createdAt, ...safeUpdates } = updates;
      void _id;
      void _createdAt;

      // `db.settings.update` recibe un UpdateSpec (Partial<AppSettings>). El
      // cast a Partial<AppSettings> es seguro porque AppSettings YA es el tipo
      // de la fila y solo se excluyeron `id` y `created_at`.
      const spec: Partial<AppSettings> = safeUpdates;
      await db.settings.update(settings.id, spec);

      // Actualizar estado local
      set({
        settings: { ...settings, ...safeUpdates }
      });

      // 🛒 TIENDA PÚBLICA: los campos de la tienda también viven en Supabase
      // (el cliente que abre el enlace NO tiene este Dexie). Se publican de
      // inmediato para que el interruptor "Abrir Tienda Digital" surta efecto.
      try {
        const { publishStoreSettings, shouldPublishSettings } = await import('@/lib/storeCloudSync');
        if (shouldPublishSettings(safeUpdates)) {
          void publishStoreSettings(safeUpdates as Record<string, unknown>);
        }
      } catch (cloudErr) {
        console.warn('No se pudo publicar la configuración de la tienda (queda en local):', cloudErr);
      }
    } catch (err) {
      console.error('Error actualizando configuración:', err);
      throw err;
    }
  },

  // ─────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────
  
  formatCurrency: (amount) => {
    const { settings } = get();
    const symbol = settings?.currency_symbol || '$';

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('COP', symbol.trim());
  },

  getTaxRate: () => get().settings?.tax_rate || 0,

  // ── 🆕 HELPERS FINANCIEROS v109 ──

  toCentavos: (amount: number): Centavos => {
    return C(BigInt(Math.round(amount * 100)));
  },

  formatCurrencyCustom: (amount: number): string => {
    const { settings } = get();
    const symbol = settings?.currency_symbol_display || CURRENCY_CONFIG.DEFAULT_SYMBOL;
    const decimalSep = settings?.decimal_separator || CURRENCY_CONFIG.DEFAULT_DECIMAL_SEPARATOR;
    const thousandSep = settings?.thousand_separator || CURRENCY_CONFIG.DEFAULT_THOUSAND_SEPARATOR;

    // Formatear con Intl usando los separadores personalizados
    const formatted = new Intl.NumberFormat('es-CO', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

    // Reemplazar separadores por defecto de Intl (,) por los configurados
    const withCustomSeparators = formatted
      .replace(/,/g, '__COMMA__')
      .replace(/\./g, '__DOT__')
      .replace(/__COMMA__/g, decimalSep)
      .replace(/__DOT__/g, thousandSep);

    return `${symbol.trim()}${withCustomSeparators}`;
  },

  getJurisdiction: (): TaxJurisdiction => {
    const { settings } = get();
    // Preferencia local (AppSettings no tiene `tax_jurisdiction`). Si una
    // instalación legacy la guardó en la fila de settings, tiene prioridad.
    const legacyJurisdiction: unknown = settings ? Reflect.get(settings, 'tax_jurisdiction') : undefined;
    if (isTaxJurisdiction(legacyJurisdiction)) return legacyJurisdiction;
    return readStoredJurisdiction();
  },

  getBranchLimit: (): number => {
    const { settings } = get();
    const plan = isPlanType(settings?.plan_type) ? settings.plan_type : 'FREE';
    const limits: Record<PlanType, number> = {
      FREE: BRANCH_LIMITS_BY_PLAN.FREE,
      NARANJA_PLUS: BRANCH_LIMITS_BY_PLAN.NARANJA_PLUS,
      ADVANCED_ACCOUNTING: BRANCH_LIMITS_BY_PLAN.ADVANCED_ACCOUNTING,
      STUDIO_FIRM: BRANCH_LIMITS_BY_PLAN.STUDIO_FIRM,
    };
    return limits[plan];
  },

  isStudioPlan: (): boolean => {
    const { settings } = get();
    // ⚠️ 'STUDIO_FIRM' pertenece a `PLANS` (src/config/constants.ts) pero NO es
    // un valor válido de `PlanType` (db.ts). Una instalación antigua puede
    // tenerlo guardado en settings; se compara sin romper el tipado y se marca
    // la necesidad de migración para alinear planes en Dexie v111.
    const plan: unknown = settings?.plan_type;
    return plan === 'STUDIO_FIRM';
  },

  getCurrencyConfig: () => {
    const { settings } = get();
    return {
      code: settings?.currency_code || CURRENCY_CONFIG.DEFAULT_CODE,
      symbol: settings?.currency_symbol_display || CURRENCY_CONFIG.DEFAULT_SYMBOL,
      decimalSeparator: settings?.decimal_separator || CURRENCY_CONFIG.DEFAULT_DECIMAL_SEPARATOR,
      thousandSeparator: settings?.thousand_separator || CURRENCY_CONFIG.DEFAULT_THOUSAND_SEPARATOR,
    };
  },
}));